import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Table, { RowCell } from 'components/Table';
import { createFragmentContainer, graphql } from 'react-relay';
import moment from 'moment';
import FilterOptions, {
  FilterOptionsState
} from 'components/Table/FilterOptions';
import CourseCompletion from 'sharedComponents/core/CourseCompletion';
import Status from 'sharedComponents/core/Table/Status';
import Button from 'components/Button/Button';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import { DocumentsTable_delegate } from './__generated__/DocumentsTable_delegate.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  courseName: {
    fontWeight: 700,
    fontSize: '0.9rem'
  }
}));

type Props = {
  delegate: DocumentsTable_delegate;
};

function DocumentsTable({ delegate }: Props) {
  const classes = useStyles();
  const action = useActionMenuDispatch();

  const [filters, setFilters] = React.useState<FilterOptionsState>({
    hideExpired: {
      label: 'Hide expired documents',
      type: 'checkbox',
      checked: true
    }
  });

  // DOCUMENTS FORMATTING
  const formatDocumentRow: RowCell<
    NonNullable<
      NonNullable<DocumentsTable_delegate['documents']>['edges']
    >[number]
  >[] = [
    {
      header: 'Record Title',
      format: (data) => {
        return <p className={classes.courseName}>{data?.name}</p>;
      }
    },
    {
      header: 'Expiry Date',
      format: (data) => {
        return data?.expiryDate
          ? moment(data?.expiryDate).format('DD MMMM YYYY')
          : '';
      }
    },
    {
      header: 'Completion Date',
      format: (data) => {
        return data?.completionDate
          ? moment(data?.completionDate).format('DD MMMM YYYY')
          : '';
      }
    },
    {
      header: 'Download',
      format: (data) => {
        return (
          <Button
            archetype="flat"
            disabled={!data?.url}
            onClick={() => data?.url && window.open(data?.url)}
          >
            {data?.url ? 'Download' : 'Not Available'}
          </Button>
        );
      }
    }
  ];

  let documents = [...(delegate?.documents?.edges ?? [])];

  if (delegate.currentBackgroundCheck?.dbsExpiryDate) {
    if (
      !(
        filters.hideExpired.checked &&
        moment(delegate.currentBackgroundCheck?.dbsExpiryDate).isBefore(
          moment()
        )
      )
    ) {
      documents.push({
        uuid: 'dbs',
        name: 'DBS Check',
        url: '',
        expiryDate: delegate.currentBackgroundCheck.dbsExpiryDate,
        completionDate: null,
        getEmail: false
      });
    }
  }

  if (filters.hideExpired.checked) {
    documents = documents.filter((doc) => {
      if (!doc?.expiryDate) return true;
      return !moment(doc.expiryDate).isBefore(moment());
    });
  }

  const onFilterClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <FilterOptions
          options={filters}
          onChange={(newFilters) => {
            setFilters(newFilters);
          }}
          title={'Document Filters'}
        />
      )
    });
  };

  return (
    <div>
      <h3>Other Records & Documents</h3>
      <Table<
        NonNullable<
          NonNullable<DocumentsTable_delegate['documents']>['edges']
        >[number]
      >
        data={documents}
        formatRow={formatDocumentRow}
        tableName="documents"
        onFilterClick={onFilterClick}
        emptyTableText="No documents to show"
        filterActive={filters.hideExpired.checked}
        fallbackLimit={5}
      />
    </div>
  );
}

const DocumentsTableFrag = createFragmentContainer(DocumentsTable, {
  delegate: graphql`
    fragment DocumentsTable_delegate on Delegate {
      currentBackgroundCheck {
        idExpiryDate
        dbsExpiryDate
      }
      documents {
        edges {
          uuid
          name
          url
          expiryDate
          completionDate
          getEmail
        }
      }
    }
  `
});

export default DocumentsTableFrag;
